import { Grid, Typography } from '@mui/material';
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { Box } from '@mui/system';
import { ThemePalette } from 'mui.theme';
import TableList from 'views/Production/components/Table/TableList';
import { FlexBox } from 'components/Containers/FlexBox';
import type { AveragrCycleTimeType } from 'types/production.types';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { convertSeconds } from '../utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  title: string;
  data: AveragrCycleTimeType[];
  isLoading: boolean;
  setHoursBreakDown: any;
}

const AverageCycleTime = ({
  enableDarkTheme,
  title,
  data,
  isLoading,
  setHoursBreakDown,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const hasChartData = data.some((obj) => obj.value && obj.value !== 0);

  const allData = {
    Loading: data.find((item) => item.name === 'Loading')?.time,
    Unloading: data.find((item) => item.name === 'Unloading')?.time,
    'Loaded Trip': data.find((item) => item.name === 'Loaded Trip')?.time,
    'Unloaded Trip': data.find((item) => item.name === 'Unloaded Trip')?.time,
  };

  useEffect(() => {
    setHoursBreakDown(allData);
  }, [allData]);
  return (
    <Grid item lg={6} md={6}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          minHeight: '300px',
          maxHeight: '300px',
          overflowY: 'auto', // Enable vertical scrolling
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            paddingTop: '10px',
          }}
        >
          {isLoading ? (
            <MultiColorCircularLoader height="300px" />
          ) : (
            <Grid container>
              <Grid item lg={6} sm={12}>
                <Box
                  sx={{
                    paddingTop: '20px',
                    minHeight: '200px',
                    maxHeight: '200px',
                  }}
                >
                  {hasChartData ? (
                    <FlexBox
                      sx={{
                        maxWidth: 'inherit',
                        height: '230px',
                        paddingTop: '20px',
                        marginRight: '10px',
                        paddingBottom: '10px',
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart width={400} height={400}>
                          <Pie
                            data={data}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={90}
                          >
                            {data.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            cursor={{ fill: '#1B2B4A' }}
                            wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                            contentStyle={{
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                              backgroundColor: enableDarkTheme
                                ? 'rgba(0,0,0, 0.6)'
                                : 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '4px',
                            }}
                            itemStyle={{
                              color: enableDarkTheme
                                ? ThemePalette.typography.wheat
                                : ThemePalette.typography.black,
                            }}
                            formatter={(value: any, name, props) => {
                              const formattedResp = convertSeconds(value);
                              return [
                                `${truncateTo2dp(formattedResp.value)} ${
                                  formattedResp.unit
                                }`,
                                name,
                              ];
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </FlexBox>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '11px',
                      }}
                    >
                      {t('no_data_available_currently')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item lg={6} sm={12}>
                {hasChartData ? (
                  <TableList enableDarkTheme={enableDarkTheme} data={data} />
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default AverageCycleTime;
